import React, { useState, useEffect, useCallback } from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getFeedback,
  getFeedbackStats,
  markAsRead,
  markAsResponded,
  deleteFeedback
} from '../services/feedback.service';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    new: { bg: 'bg-blue-500/10', text: 'text-blue-400', icon: 'mail', label: 'New' },
    read: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: 'drafts', label: 'Read' },
    responded: { bg: 'bg-green-500/10', text: 'text-green-400', icon: 'reply', label: 'Responded' }
  };

  const statusConfig = config[status] || config.new;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
      <span className="material-symbols-outlined text-sm">{statusConfig.icon}</span>
      {statusConfig.label}
    </span>
  );
};

// Rating Stars Component
const RatingStars = ({ rating }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`material-symbols-outlined text-lg ${star <= rating ? 'text-amber-400' : 'text-white/10'}`}
        >
          star
        </span>
      ))}
      <span className="ml-1 text-sm text-white/40">({rating})</span>
    </div>
  );
};

// View Feedback Modal
const ViewModal = ({ isOpen, onClose, feedback, onMarkRead, onMarkResponded, isLoading, getFeedbackId }) => {
  if (!isOpen || !feedback) return null;
  const feedbackId = getFeedbackId ? getFeedbackId(feedback) : (feedback?.id ?? feedback?.feedback_id);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-fade-in">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Feedback Details</h3>
            <button onClick={onClose} className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)] space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500/30 to-teal-600/30 flex items-center justify-center text-white font-medium">
                {feedback.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1">
                <p className="font-medium text-white">{feedback.name || 'Anonymous'}</p>
                <p className="text-sm text-white/40">{feedback.email || 'No email provided'}</p>
              </div>
              <StatusBadge status={feedback.status} />
            </div>
            {feedback.place && (
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                <p className="text-sm text-white/40 mb-1">About Place</p>
                <Link to={`/places/edit/${feedback.place.id}`} className="font-medium text-purple-400 hover:text-purple-300 flex items-center gap-2">
                  <span className="material-symbols-outlined text-lg">location_on</span>
                  {feedback.place.name || feedback.place.title}
                </Link>
              </div>
            )}
            {feedback.rating && (
              <div>
                <p className="text-sm text-white/40 mb-2">Overall Rating</p>
                <RatingStars rating={feedback.rating} />
              </div>
            )}
            {feedback.liked_content && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-green-400">thumb_up</span>
                  <p className="text-sm font-medium text-white/70">What they liked</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                  <p className="text-white/70 whitespace-pre-wrap">{feedback.liked_content}</p>
                </div>
              </div>
            )}
            {feedback.improvement_suggestions && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-amber-400">lightbulb</span>
                  <p className="text-sm font-medium text-white/70">Suggestions for improvement</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                  <p className="text-white/70 whitespace-pre-wrap">{feedback.improvement_suggestions}</p>
                </div>
              </div>
            )}
            <p className="text-sm text-white/30">Received: {new Date(feedback.created_at).toLocaleString('en-IN')}</p>
          </div>
          <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between gap-3">
            <div className="flex gap-2">
              {feedback.status === 'new' && (
                <button
                  onClick={() => onMarkRead(feedbackId)}
                  disabled={isLoading}
                  className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-xl font-medium hover:bg-amber-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span> : <span className="material-symbols-outlined text-lg">drafts</span>}
                  Mark as Read
                </button>
              )}
              {feedback.status !== 'responded' && (
                <button
                  onClick={() => onMarkResponded(feedbackId)}
                  disabled={isLoading}
                  className="px-4 py-2 bg-green-500/20 text-green-400 rounded-xl font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span> : <span className="material-symbols-outlined text-lg">check</span>}
                  Mark as Responded
                </button>
              )}
            </div>
            <button onClick={onClose} className="px-4 py-2 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-sm p-6 animate-fade-in">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-red-400">warning</span>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Delete Feedback</h3>
            <p className="text-white/50">Are you sure you want to delete this feedback? This action cannot be undone.</p>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={onClose} disabled={isLoading} className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={onConfirm} disabled={isLoading} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {isLoading ? (<><span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>Deleting...</>) : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function Feedback() {
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, responded: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Filters (persisted so refresh keeps selection)
  const [statusFilter, setStatusFilter] = usePersistedState('feedback_status', '');

  // Modals
  const [viewModal, setViewModal] = useState({ isOpen: false, feedback: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, feedbackId: null });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const fetchFeedback = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (statusFilter) params.status = statusFilter;

      const response = await getFeedback(params);
      setFeedbackList(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await getFeedbackStats();
      setStats(response.data || response);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getFeedbackId = (feedback) => feedback?.id ?? feedback?.feedback_id;

  const handleMarkRead = async (id) => {
    try {
      setActionLoading(id);
      await markAsRead(id);
      toast.success('Marked as read');
      
      // Update local state (match by id or feedback_id)
      setFeedbackList(prev =>
        prev.map(f => (f.id ?? f.feedback_id) === id ? { ...f, status: 'read' } : f)
      );
      setViewModal(prev => (prev.feedback?.id ?? prev.feedback?.feedback_id) === id ? { ...prev, feedback: { ...prev.feedback, status: 'read' } } : prev);
      fetchStats();
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Failed to mark as read');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkResponded = async (id) => {
    try {
      setActionLoading(id);
      await markAsResponded(id);
      toast.success('Marked as responded');
      
      // Update local state (match by id or feedback_id)
      setFeedbackList(prev =>
        prev.map(f => (f.id ?? f.feedback_id) === id ? { ...f, status: 'responded' } : f)
      );
      setViewModal(prev => (prev.feedback?.id ?? prev.feedback?.feedback_id) === id ? { ...prev, feedback: { ...prev.feedback, status: 'responded' } } : prev);
      fetchStats();
    } catch (error) {
      console.error('Error marking as responded:', error);
      toast.error('Failed to mark as responded');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.feedbackId) return;

    try {
      setActionLoading(deleteModal.feedbackId);
      await deleteFeedback(deleteModal.feedbackId);
      toast.success('Feedback deleted');
      setDeleteModal({ isOpen: false, feedbackId: null });
      fetchFeedback();
      fetchStats();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast.error('Failed to delete feedback');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (isLoading && feedbackList.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-indigo-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extralight tracking-tight text-white">
          Feedback<span className="text-purple-400 font-bold">.</span>
        </h1>
        <p className="text-sm text-white/40 mt-1">Manage user feedback and suggestions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-purple-400">inbox</span>
            </div>
            <div>
              <p className="text-2xl font-light text-white">{stats.total || 0}</p>
              <p className="text-xs text-white/40">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-blue-400">mail</span>
            </div>
            <div>
              <p className="text-2xl font-light text-white">{stats.new || 0}</p>
              <p className="text-xs text-white/40">New</p>
            </div>
          </div>
        </div>
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-amber-400">drafts</span>
            </div>
            <div>
              <p className="text-2xl font-light text-white">{stats.read || 0}</p>
              <p className="text-xs text-white/40">Read</p>
            </div>
          </div>
        </div>
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-green-400">reply</span>
            </div>
            <div>
              <p className="text-2xl font-light text-white">{stats.responded || 0}</p>
              <p className="text-xs text-white/40">Responded</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50 transition-all"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="responded">Responded</option>
        </select>
        {statusFilter && (
          <button
            onClick={() => setStatusFilter('')}
            className="px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-xl font-medium transition-colors flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">close</span>
            Clear
          </button>
        )}
      </div>

      {/* Feedback Blocks Grid */}
      <div className="space-y-4">
        <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider">
          Feedback ({feedbackList.length}{pagination.total > 0 ? ` of ${pagination.total}` : ''})
        </h2>
        {feedbackList.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {feedbackList.map((feedback) => (
              <div
                key={getFeedbackId(feedback) ?? `fb-${feedback.created_at}-${feedback.email}`}
                className={`group bg-white/[0.02] backdrop-blur-sm border rounded-2xl p-5 hover:border-white/10 hover:bg-white/[0.04] transition-all duration-200 cursor-pointer ${feedback.status === 'new' ? 'border-blue-500/20' : 'border-white/5'}`}
                onClick={() => setViewModal({ isOpen: true, feedback })}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/30 to-teal-600/30 flex items-center justify-center text-white font-medium flex-shrink-0">
                      {feedback.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white truncate">{feedback.name || 'Anonymous'}</p>
                      <p className="text-sm text-white/40 truncate">{feedback.email || 'No email'}</p>
                      <StatusBadge status={feedback.status} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setViewModal({ isOpen: true, feedback }); }}
                      className="p-2 text-white/40 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                      title="View"
                    >
                      <span className="material-symbols-outlined text-lg">visibility</span>
                    </button>
                    {feedback.status === 'new' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMarkRead(getFeedbackId(feedback)); }}
                        disabled={actionLoading === getFeedbackId(feedback)}
                        className="p-2 text-white/40 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Mark as Read"
                      >
                        {actionLoading === getFeedbackId(feedback) ? <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span> : <span className="material-symbols-outlined text-lg">drafts</span>}
                      </button>
                    )}
                    {feedback.status !== 'responded' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMarkResponded(getFeedbackId(feedback)); }}
                        disabled={actionLoading === getFeedbackId(feedback)}
                        className="p-2 text-white/40 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Mark as Responded"
                      >
                        <span className="material-symbols-outlined text-lg">reply</span>
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteModal({ isOpen: true, feedbackId: getFeedbackId(feedback) }); }}
                      disabled={actionLoading === getFeedbackId(feedback)}
                      className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      <span className="material-symbols-outlined text-lg">delete</span>
                    </button>
                  </div>
                </div>
                {feedback.place && (
                  <div className="text-sm text-purple-400 flex items-center gap-1 mb-2">
                    <span className="material-symbols-outlined text-sm">location_on</span>
                    <span className="truncate">{feedback.place.name || feedback.place.title}</span>
                  </div>
                )}
                <div className="space-y-1 mb-3">
                  {feedback.liked_content && (
                    <p className="text-sm text-white/50 flex items-start gap-2 line-clamp-1">
                      <span className="material-symbols-outlined text-green-400 text-sm flex-shrink-0">thumb_up</span>
                      {truncateText(feedback.liked_content, 60)}
                    </p>
                  )}
                  {feedback.improvement_suggestions && (
                    <p className="text-sm text-white/50 flex items-start gap-2 line-clamp-1">
                      <span className="material-symbols-outlined text-amber-400 text-sm flex-shrink-0">lightbulb</span>
                      {truncateText(feedback.improvement_suggestions, 60)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                  {feedback.rating ? <RatingStars rating={feedback.rating} /> : null}
                  <span className="text-xs text-white/30">{formatDate(feedback.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-white/20">inbox</span>
            </div>
            <p className="font-medium text-white mb-1">No feedback found</p>
            <p className="text-white/40 text-sm">
              {statusFilter ? 'Try changing the filter' : 'No feedback received yet'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-white/40">Showing {feedbackList.length} of {pagination.total} feedback entries</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-white/40">Page {pagination.page} of {pagination.totalPages}</span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      <ViewModal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, feedback: null })}
        feedback={viewModal.feedback}
        onMarkRead={handleMarkRead}
        onMarkResponded={handleMarkResponded}
        isLoading={actionLoading === getFeedbackId(viewModal.feedback)}
        getFeedbackId={getFeedbackId}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, feedbackId: null })}
        onConfirm={handleDelete}
        isLoading={actionLoading === deleteModal.feedbackId}
      />
    </div>
  );
}

export default Feedback;

